import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  MessageSquare, 
  Download,
  ChevronLeft,
  ChevronRight,
  Search,
  ArrowUpDown
} from 'lucide-react';
import { format } from 'date-fns';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const API_URL = "";

const FileDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [xColumn, setXColumn] = useState('');
  const [yColumn, setYColumn] = useState('');
  const [chartData, setChartData] = useState([]);
  const [chartType, setChartType] = useState('bar');

  useEffect(() => {
    fetchFileDetails();
  }, [id]);

  useEffect(() => {
    fetchFileData();
  }, [id, page]);

  useEffect(() => {
    if (xColumn && yColumn) {
      fetchChartData();
    }
  }, [xColumn, yColumn]);

  const fetchFileDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/files/${id}`, {
        withCredentials: true
      });
      setFile(response.data);
      if (response.data.columns?.length >= 2) {
        setXColumn(response.data.columns[0]);
        // Find first numeric-looking column for Y
        const numericCol = response.data.columns.find((col, idx) => idx > 0);
        if (numericCol) setYColumn(numericCol);
      }
    } catch (e) {
      console.error('Error fetching file details:', e);
    }
  };

  const fetchFileData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/files/${id}/data`, {
        params: { limit: pageSize, offset: page * pageSize },
        withCredentials: true
      });
      setData(response.data.data);
      setTotalRecords(response.data.total);
    } catch (e) {
      console.error('Error fetching file data:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/chart-data/${id}`, {
        params: { x_column: xColumn, y_column: yColumn },
        withCredentials: true
      });
      setChartData(response.data.chart_data);
    } catch (e) {
      console.error('Error fetching chart data:', e);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortColumn) return 0;
    const aVal = a[sortColumn];
    const bVal = b[sortColumn];
    if (aVal === bVal) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const comparison = aVal < bVal ? -1 : 1;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const filteredData = sortedData.filter(row => {
    if (!searchQuery) return true;
    return Object.values(row).some(val => 
      String(val).toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const totalPages = Math.ceil(totalRecords / pageSize);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!file) {
    return (
      <div className="p-6">
        <Card className="border-zinc-200 bg-white">
          <CardContent className="p-12 text-center">
            <p className="text-zinc-500">File not found</p>
            <Button onClick={() => navigate('/files')} className="mt-4">
              Back to Files
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="file-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/files')}
            className="text-zinc-600"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-zinc-950">
              {file.filename}
            </h1>
            <p className="text-zinc-500 text-sm">
              {file.row_count?.toLocaleString()} rows • {file.columns?.length} columns • 
              Uploaded {file.created_at ? format(new Date(file.created_at), 'MMM d, yyyy HH:mm') : '-'}
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate(`/chat?file=${id}`)}
          className="bg-blue-700 hover:bg-blue-800 text-white"
          data-testid="chat-with-file-button"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Chat with AI
        </Button>
      </div>

      {/* Chart Section */}
      {file.columns?.length >= 2 && file.file_type !== 'pdf' && (
        <Card className="border-zinc-200 bg-white mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-zinc-950">
                Data Visualization
              </CardTitle>
              <div className="flex items-center gap-3">
                <Select value={chartType} onValueChange={setChartType}>
                  <SelectTrigger className="w-28 border-zinc-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bar">Bar Chart</SelectItem>
                    <SelectItem value="line">Line Chart</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-1 block">
                  X-Axis
                </label>
                <Select value={xColumn} onValueChange={setXColumn}>
                  <SelectTrigger className="border-zinc-200">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {file.columns?.map((col) => (
                      <SelectItem key={col} value={col}>{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-1 block">
                  Y-Axis
                </label>
                <Select value={yColumn} onValueChange={setYColumn}>
                  <SelectTrigger className="border-zinc-200">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {file.columns?.map((col) => (
                      <SelectItem key={col} value={col}>{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="h-64">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === 'bar' ? (
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                      <XAxis dataKey="x" tick={{ fontSize: 11 }} stroke="#71717a" angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 12 }} stroke="#71717a" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e4e4e7',
                          borderRadius: '8px'
                        }} 
                      />
                      <Bar dataKey="y" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  ) : (
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                      <XAxis dataKey="x" tick={{ fontSize: 11 }} stroke="#71717a" angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 12 }} stroke="#71717a" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e4e4e7',
                          borderRadius: '8px'
                        }} 
                      />
                      <Line type="monotone" dataKey="y" stroke="#1d4ed8" strokeWidth={2} dot={{ fill: '#1d4ed8', r: 4 }} />
                    </LineChart>
                  )}
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-400">
                  Select columns to visualize data
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      <Card className="border-zinc-200 bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-zinc-950">
              Data Preview
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search in data..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-zinc-200 h-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full data-table text-sm" data-testid="data-table">
              <thead>
                <tr className="border-b border-zinc-200">
                  {file.columns?.map((col) => (
                    <th 
                      key={col}
                      className="text-left py-3 px-4 text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 cursor-pointer hover:text-zinc-700"
                      onClick={() => handleSort(col)}
                    >
                      <span className="flex items-center gap-1">
                        {col}
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-zinc-100 hover:bg-zinc-50">
                    {file.columns?.map((col) => (
                      <td key={col} className="py-2 px-4 text-zinc-700 max-w-xs truncate">
                        {row[col] != null ? String(row[col]) : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-200">
            <p className="text-sm text-zinc-500">
              Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalRecords)} of {totalRecords} records
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-zinc-200"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-zinc-600">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="border-zinc-200"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FileDetailPage;
